import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Conversation, Profile } from '@/types/chat';
import { useToast } from '@/components/ui/use-toast';

export const useChat = () => {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Real-time subscription for conversations updates
    useEffect(() => {
        const channel = supabase
            .channel('chat_conversations_updates')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'chat_conversations',
                },
                () => {
                    queryClient.invalidateQueries({ queryKey: ['chat_conversations'] });
                }
            )
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'chat_messages',
                },
                () => {
                    // Also update conversations when new messages arrive (for last_message preview)
                    queryClient.invalidateQueries({ queryKey: ['chat_conversations'] });
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [queryClient]);

    const fetchConversations = async (): Promise<Conversation[]> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        // Get conversations where the user is a participant
        const { data: participations, error: participantsError } = await supabase
            .from('chat_participants')
            .select('conversation_id, last_read_at')
            .eq('user_id', user.id);

        if (participantsError) throw participantsError;

        const conversationIds = participations.map(p => p.conversation_id);

        if (conversationIds.length === 0) return [];

        const { data, error } = await supabase
            .from('chat_conversations')
            .select('*')
            .in('id', conversationIds)
            .order('last_message_at', { ascending: false });

        if (error) throw error;

        // Fetch details for each conversation (participants, last message, unread count)
        const conversationsWithDetails = await Promise.all(data.map(async (conv) => {
            // Get participants
            const { data: participantsData } = await supabase
                .from('chat_participants')
                .select('user_id')
                .eq('conversation_id', conv.id);

            const participantIds = participantsData?.map(p => p.user_id) || [];

            // Fetch profiles
            let profiles: Profile[] = [];
            if (participantIds.length > 0) {
                const { data: profilesData } = await supabase
                    .from('profiles')
                    .select('id, name, avatar_url, email')
                    .in('id', participantIds);

                if (profilesData) {
                    profiles = profilesData as Profile[];
                }
            }

            // Get last message
            const { data: lastMsg } = await supabase
                .from('chat_messages')
                .select('*')
                .eq('conversation_id', conv.id)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            // Calculate unread count
            const myParticipation = participations.find(p => p.conversation_id === conv.id);
            const lastReadAt = myParticipation?.last_read_at || '1970-01-01T00:00:00.000Z';

            const { count: unreadCount } = await supabase
                .from('chat_messages')
                .select('*', { count: 'exact', head: true })
                .eq('conversation_id', conv.id)
                .gt('created_at', lastReadAt)
                .neq('sender_id', user.id);

            return {
                ...conv,
                last_message: lastMsg || undefined,
                participants: profiles,
                unread_count: unreadCount || 0
            } as Conversation;
        }));

        return conversationsWithDetails;
    };

    const { data: conversations, isLoading: isLoadingConversations } = useQuery({
        queryKey: ['chat_conversations'],
        queryFn: fetchConversations,
    });

    const sendMessageMutation = useMutation({
        mutationFn: async ({ conversationId, content, type = 'text', attachmentUrl }: { conversationId: string, content: string, type?: 'text' | 'image' | 'file', attachmentUrl?: string }) => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            const { data, error } = await supabase
                .from('chat_messages')
                .insert({
                    conversation_id: conversationId,
                    sender_id: user.id,
                    content,
                    type,
                    attachment_url: attachmentUrl
                })
                .select()
                .single();

            if (error) throw error;

            // Update conversation last_message_at
            await supabase
                .from('chat_conversations')
                .update({ last_message_at: new Date().toISOString() })
                .eq('id', conversationId);

            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['chat_conversations'] });
            queryClient.invalidateQueries({ queryKey: ['chat_messages'] });
        },
        onError: (error) => {
            toast({
                title: "Erro ao enviar mensagem",
                description: error.message,
                variant: "destructive"
            });
        }
    });

    const createConversationMutation = useMutation({
        mutationFn: async ({ participantIds, type, name }: { participantIds: string[], type: 'direct' | 'group', name?: string }) => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            // Create conversation
            const { data: conversation, error: convError } = await supabase
                .from('chat_conversations')
                .insert({ type, name, created_by: user.id, last_message_at: new Date().toISOString() })
                .select()
                .single();

            if (convError) throw convError;

            // Add participants (including self)
            const allParticipants = [...new Set([...participantIds, user.id])];
            const participantsData = allParticipants.map(id => ({
                conversation_id: conversation.id,
                user_id: id
            }));

            const { error: partError } = await supabase
                .from('chat_participants')
                .insert(participantsData);

            if (partError) throw partError;

            return conversation;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['chat_conversations'] });
            toast({
                title: "Conversa criada",
                description: "Você pode começar a enviar mensagens."
            });
        },
        onError: (error) => {
            console.error('Erro ao criar conversa:', error);
            toast({
                title: "Erro ao criar conversa",
                description: error.message || "Ocorreu um erro inesperado.",
                variant: "destructive"
            });
        }
    });

    const deleteConversationMutation = useMutation({
        mutationFn: async (conversationId: string) => {
            const { error } = await supabase
                .from('chat_conversations')
                .delete()
                .eq('id', conversationId);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['chat_conversations'] });
            toast({
                title: "Conversa apagada",
                description: "A conversa foi removida com sucesso."
            });
        },
        onError: (error) => {
            toast({
                title: "Erro ao apagar conversa",
                description: error.message,
                variant: "destructive"
            });
        }
    });

    const markAsReadMutation = useMutation({
        mutationFn: async (conversationId: string) => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { error } = await supabase
                .from('chat_participants')
                .update({ last_read_at: new Date().toISOString() })
                .eq('conversation_id', conversationId)
                .eq('user_id', user.id);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['chat_conversations'] });
        }
    });

    return {
        conversations,
        isLoadingConversations,
        sendMessage: sendMessageMutation.mutate,
        createConversation: createConversationMutation.mutate,
        deleteConversation: deleteConversationMutation.mutate,
        markAsRead: markAsReadMutation.mutate,
        isSending: sendMessageMutation.isPending
    };
};
